import { useCallback, useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Sidebar } from "@/components/layout/Sidebar";
import { Topbar } from "@/components/layout/Topbar";
import { CommandPalette } from "@/components/search/CommandPalette";
import { AssistantPanel } from "@/components/assistant/AssistantPanel";
import { QuickCreateModal } from "@/components/quick/QuickCreateModal";
import { useAuth } from "@/context/AuthContext";
import { termRules } from "@/lib/workspace";
import { buildConverter, startTermTranslator } from "@/lib/termTranslator";

const useTermTranslator = () => {
  const { me } = useAuth();
  const key = JSON.stringify(me?.workspace?.labels || {});
  useEffect(() => {
    return startTermTranslator(buildConverter(termRules(JSON.parse(key))));
  }, [key]);
};

export default function AppShell() {
  const [navOpen, setNavOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [createType, setCreateType] = useState(null);
  useTermTranslator();

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const openCreate = useCallback((t) => setCreateType(t), []);

  return (
    <div className="flex h-screen overflow-hidden bg-canvas" data-testid="app-shell">
      <aside className="hidden w-64 shrink-0 border-r border-line bg-surface lg:flex">
        <Sidebar />
      </aside>
      <Sheet open={navOpen} onOpenChange={setNavOpen}>
        <SheetContent side="left" className="w-72 p-0" data-testid="mobile-nav-sheet">
          <SheetTitle className="sr-only">Navigazione</SheetTitle>
          <Sidebar onNavigate={() => setNavOpen(false)} />
        </SheetContent>
      </Sheet>
      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar onMenu={() => setNavOpen(true)} onSearch={() => setSearchOpen(true)}
          onAssistant={() => setAssistantOpen(true)} onCreate={openCreate} />
        <main className="flex-1 overflow-y-auto" data-testid="main-content">
          <div className="mx-auto max-w-[1400px] p-4 sm:p-6 lg:p-8">
            <Outlet context={{ openCreate, openAssistant: () => setAssistantOpen(true) }} />
          </div>
        </main>
      </div>
      <CommandPalette open={searchOpen} onOpenChange={setSearchOpen} onCreate={openCreate} />
      <AssistantPanel open={assistantOpen} onOpenChange={setAssistantOpen} />
      <QuickCreateModal type={createType} onClose={() => setCreateType(null)} />
    </div>
  );
}
